type QueryDebit = {
  id: string;
  claimID: string | null;
  payorID: string | null;
  tariffID: string | null;
  source: string | null;
  status: string | null;
  microfilm: string | null;
  createdAt: string | Date;
};

type QueryClaim = {
  id: string;
  payorID?: string | null;
  payor?: { name: string } | null;
  tariffs: { id: string }[];
  debits?: QueryDebit[];
  claimQueries?: { status: string }[];
};

export type ClaimQueryEligibilityError =
  | 'notManitoba'
  | 'unprocessed'
  | 'pending'
  | 'openQuery';

export const getClaimQueryEligibilityError = (
  claim: QueryClaim,
  debitTimestamp: (value: string | Date) => number
): ClaimQueryEligibilityError | null => {
  if (claim.payor?.name !== 'MB') return 'notManitoba';
  const tariffIDs = new Set(claim.tariffs.map((tariff) => tariff.id));
  const debits = (claim.debits ?? []).filter(
    (debit) =>
      debit.claimID === claim.id &&
      debit.payorID === claim.payorID &&
      debit.source === 'REMITTANCE' &&
      debit.tariffID !== null &&
      tariffIDs.has(debit.tariffID)
  );
  // A processed Manitoba remittance is evidence of submission and adjudication;
  // a manual payment or a locally edited claim status is not.
  if (
    !debits.some(
      (debit) => debit.status === 'processed' && debit.microfilm?.trim()
    )
  ) {
    return 'unprocessed';
  }
  const latestByTariff = new Map<string, QueryDebit>();
  for (const debit of debits) {
    if (!debit.tariffID) continue;
    const previous = latestByTariff.get(debit.tariffID);
    if (
      !previous ||
      debitTimestamp(debit.createdAt) > debitTimestamp(previous.createdAt) ||
      (debitTimestamp(debit.createdAt) === debitTimestamp(previous.createdAt) &&
        debit.id.localeCompare(previous.id) > 0)
    ) {
      latestByTariff.set(debit.tariffID, debit);
    }
  }
  if (
    Array.from(latestByTariff.values()).some(
      (debit) => debit.status === 'pending'
    )
  )
    return 'pending';
  if (
    claim.claimQueries?.some((query) =>
      ['CREATED', 'SUBMITTED', 'PENDING'].includes(query.status)
    )
  )
    return 'openQuery';
  return null;
};
